/**
 * CloudFront signed-cookie delivery wiring.
 *
 * Transaction bodies (and other cold payloads) live in S3 and are served ONLY
 * through CloudFront — the bucket rejects unauthenticated reads. Access is gated
 * by short-lived signed cookies minted by the authentication service against the
 * caller's identity, so every read is attributable and revocable. This wires the
 * shared `@authe/fe-commons/storage` CDN session to the dashboard's `ApiClient`:
 * the mint call rides the same region-routed, bearer-authenticated client but
 * with `credentials:"include"` so the browser stores the `CloudFront-*` cookies.
 *
 * The session helper mints lazily, caches the decision + expiry, and re-mints
 * before expiry; a failed/disabled mint degrades to `signedCookieDelivery:false`
 * so body loading falls back to the authenticated `/transactions/:id/body`
 * endpoint or an inline body.
 */
import type { ApiClient } from "@authe/fe-commons/api";
import { configureCdnDelivery, createScopedStorage, type CdnMintResult } from "@authe/fe-commons/storage";
import { tenantContextFromClaims } from "@authe/fe-commons/auth";
import { appConfig, appEnv } from "@/config";
import { getRoutingToken } from "./routing-token";

/** Storage name for the persisted CDN mint decision (scoped per identity). */
const CDN_SESSION_NAME = "cdn-session";

/**
 * An identity-scoped store for the persisted mint decision, derived lazily from
 * the live routing token. Returns null until an identity is known (no persist),
 * and is keyed by the full tenancy quadruple + user so a different
 * user/tenant/org never reuses another's cookie decision (the `CloudFront-*`
 * cookies are minted per identity). Returns null when no `user_id` is resolved
 * yet so we never persist against an anonymous key.
 */
function cdnSessionStore() {
  const token = getRoutingToken();
  if (!token) return null;
  const t = tenantContextFromClaims(token as Record<string, unknown>, appEnv.claimsNamespace);
  if (!t.userId) return null;
  return createScopedStorage({
    app: "dashboard",
    customerId: t.customerId,
    orgId: t.orgId,
    tenantId: t.tenantId,
    userId: t.userId,
  });
}

/** Raw shape of the authentication mint endpoint's result envelope payload. */
interface MintEnvelope {
  signed_cookie_delivery?: boolean;
  cdn_base_url?: string;
  resource_prefix?: string;
  expires_at?: number;
  reason?: string;
  /**
   * Level-tagged PRIVATE key prefixes the signed cookie authorises (the
   * customer/org/tenant/user trees under `pipeline/bodies/private/{c|o|t|u}/…`).
   * Carrying these client-side lets the body loader refuse a read for another
   * scope's object BEFORE firing a request that can only 403 (`MissingKey`).
   */
  private_prefixes?: string[];
  /** Shared (cross-tenant, cookie-covered) prefixes (e.g. `transaction-bodies`, `saved-views/private`). */
  shared_prefixes?: string[];
  /**
   * Level-tagged PRIVATE ASSET key prefixes the signed cookie authorises (the
   * per-scope branding/logo trees under `assets/private/{c|o|t|m|u}/…`). Lets the
   * logo loader refuse a cross-scope read before firing a doomed request.
   */
  private_asset_prefixes?: string[];
}

function stringArray(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const out = value.filter((v): v is string => typeof v === "string" && v.length > 0);
  return out.length > 0 ? out : undefined;
}

/**
 * Configure signed-cookie CDN delivery for the app. Call once at bootstrap with
 * the shared `ApiClient`. The CDN origin defaults to `appConfig.cdnUrl`, but a
 * successful mint overrides it with the backend's authoritative `cdn_base_url`.
 */
export function configureCdnForApp(apiClient: ApiClient): void {
  configureCdnDelivery({
    cdnBaseUrl: appConfig.cdnUrl,
    mint: async (): Promise<CdnMintResult> => {
      const res = await apiClient.post<MintEnvelope>("/mint-cdn-cookies", undefined, {
        credentials: "include",
        // A failed mint must not retry-storm or block body loading; one shot.
        retries: 0,
        cacheTtlMs: 0,
      });
      if (!res.ok || !res.data) {
        return { signedCookieDelivery: false, reason: "mint_unavailable" };
      }
      const d = res.data;
      return {
        signedCookieDelivery: Boolean(d.signed_cookie_delivery),
        cdnBaseUrl: d.cdn_base_url,
        resourcePrefix: d.resource_prefix,
        expiresAt: d.expires_at,
        reason: d.reason,
        // Surface the caller's authorised scopes so the body loader's
        // client-side guard ({@link cdnKeyDenialReason}) can short-circuit a
        // cross-scope read instead of letting it 403 at the edge.
        privatePrefixes: stringArray(d.private_prefixes),
        sharedPrefixes: stringArray(d.shared_prefixes),
        privateAssetPrefixes: stringArray(d.private_asset_prefixes),
      };
    },
    // The minted cookies cover a whole path and outlive a reload, so persist the
    // decision (identity-scoped) and reuse it instead of re-minting every load.
    persist: {
      load: () => cdnSessionStore()?.getAt<CdnMintResult | null>(CDN_SESSION_NAME, "user", null) ?? null,
      save: (result) => {
        cdnSessionStore()?.set(CDN_SESSION_NAME, result);
      },
      clear: () => {
        cdnSessionStore()?.remove(CDN_SESSION_NAME);
      },
    },
  });
}
